import { useMutation } from "@apollo/client";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { AddFulfillmentNoteDialog, FacilityRowData } from "components/AddFulfillmentNoteDialog";
import { useAlerts } from "components/Alerts/AlertProvider";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { useBulkActionContext } from "./BulkActionsMenu";

const UPDATE_FULFILLMENT_NOTES_BULK = gql(`
  mutation UpdateWorkflowFulfillmentNotesBulk($reportIds: [ID!]!, $note: String) {
    updateTierIIReportsFulfillmentNoteBulk(reportIds: $reportIds, note: $note)
  }
`);

export function AddFulfillmentNoteMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("addFulfillmentNote")}>
      <ListItemIcon>
        <StickyNote2Outlined />
      </ListItemIcon>
      <ListItemText>Add Fulfillment Note</ListItemText>
    </MenuItem>
  );
}

export function AddFulfillmentNoteDialog_() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows, clearSelection } = useBulkActionContext();

  const [updateNotes, { loading }] = useMutation(UPDATE_FULFILLMENT_NOTES_BULK, {
    refetchQueries: ["WorkflowActivities"],
  });

  const facilities: FacilityRowData[] = useMemo(
    () =>
      selectedRows
        .filter((row) => hasValue(row.tierIIReportId))
        .map((row) => ({
          reportId: row.tierIIReportId,
          organizationName: row.organizationName ?? "",
          facilityName: row.name ?? "",
          facilityAddress: row.facilityAddress ?? "",
          facilityId: row.tierIIReportId ?? "",
          tenantId: row.tenantId ?? undefined,
        })),
    [selectedRows]
  );

  const reportIds = useMemo(
    () => selectedRows.map((row) => row.tierIIReportId).filter(hasValue),
    [selectedRows]
  );

  const handleSubmit = useCallback(
    async (note: string) => {
      try {
        const result = await updateNotes({
          variables: {
            reportIds,
            note: note || null,
          },
        });

        const updatedCount = result.data?.updateTierIIReportsFulfillmentNoteBulk ?? 0;
        alerts.success(
          `Successfully updated fulfillment note for ${updatedCount} ${pluralize(
            "report",
            updatedCount
          )}`
        );
        clearSelection();
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating fulfillment notes", err);
      }
    },
    [updateNotes, reportIds, alerts, onClose, clearSelection]
  );

  if (dialogState !== "addFulfillmentNote") {
    return null;
  }

  return (
    <AddFulfillmentNoteDialog
      open={true}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      facilities={facilities}
    />
  );
}
